/*
 * Copyright (c) 2025, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { useEffect, useState } from "react";
import { Api, ApiKeysReducer, ApplicationState, UserReducer } from "../../../data/interfaces";
import { Grid, Container, Typography, Box } from "@material-ui/core";
import { FormattedMessage } from "react-intl";
import CustomIcons from "../../../icons/CustomIcons";
import { useStyles } from "./style";
import { PrimaryButtonRounded } from "../../../elements/PrimaryButtonRounded";
import { AddRounded } from "@material-ui/icons";
import { connect } from "react-redux";
import { fetchApiKeysList } from "../../../data/operations";
import { PreLoaderLogo } from "../../../elements/PreLoaderLogo";


interface GenerateFirstApiKeyProps {
    apiInfo: Api,
    apiKeysReducer: ApiKeysReducer,
    userReducer: UserReducer
    keyType: string,

    fetchApiKeys: (apiId: string, applicationId: string, organizationId: string, keyType: string) => void,
    onGenerateKey: () => void
}

function GenerateFirstApiKey(props: GenerateFirstApiKeyProps) {
    const classes = useStyles();
    const { apiKeysReducer, userReducer, apiInfo, keyType, fetchApiKeys, onGenerateKey} = props;
    const [isGenerateFirstApiKeyLoading, setIsGenerateFirstApiKeyLoading] = useState(true);

    const { selectedOrg } = userReducer;

    const apiKeys = apiKeysReducer?.apiKeys;
    
    const isLoading = apiKeysReducer.isLoading;
    const [isApiKeysFetchingInitiated, setIsApiKeysFetchingInitiated] = useState(false);

    useEffect(() => {
        setIsApiKeysFetchingInitiated(true);
        fetchApiKeys(apiInfo.id, "", selectedOrg.uuid, keyType);
    }, [apiInfo, selectedOrg, keyType]);

    useEffect(() => {
        if (isApiKeysFetchingInitiated && !apiKeysReducer.isLoading) {
            setIsGenerateFirstApiKeyLoading(false);
            setIsApiKeysFetchingInitiated(false);
        }
    }, [apiKeysReducer.isLoading]);

    const CreateBtn = () => {
        return (
            <PrimaryButtonRounded className={classes.createBtn}
                dataTestId="generate-api-key-btn"
                onClick={onGenerateKey}
                startIcon={<AddRounded />}
                text={"Generate API Key"}
            />
        );
    };

    if (isGenerateFirstApiKeyLoading) {
        return null;
    }

    if (isLoading) {
        return null;
    }

    if (apiKeys.length > 0) {
        return null;
    }

    return (
        <>
            <Grid container={true} justifyContent="center" direction="row" alignItems="center">
                <Grid item={true} xs={12}>
                    <Container fixed={true}>
                        <div className={classes.imgContainer}>
                            <CustomIcons icon='create-with-choreo' />
                        </div>
                        <div className={classes.primaryTitleContainer}>
                            <Typography
                                component="div"
                                className={classes.primaryTitle}
                                gutterBottom={true}
                            >
                                <FormattedMessage
                                    id='api.apiCredentials.generateFirstApiKey.title'
                                    defaultMessage='Time to create your API Key'
                                />
                            </Typography>
                        </div>
                        <Typography variant="subtitle1" component="div" className={classes.secondaryTitle}>
                            <FormattedMessage
                                id='api.apiCredentials.generateFirstApiKey.subtitle'
                                defaultMessage='You don&apos;t have any API Keys Generated'
                            />
                        </Typography>
                        <CreateBtn />
                    </Container>
                </Grid>
            </Grid>
        </>
    );
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        apiKeysReducer: state.apiKeysReducer,
        userReducer: state.userReducer
    }
}

const mapDispatchToProps = {
    fetchApiKeys: fetchApiKeysList
}

export default connect(mapStateToProps, mapDispatchToProps)(GenerateFirstApiKey);
